import type { Event } from "@/types";
import { Text } from "@components/atoms";
import { MapPin, Calendar } from "lucide-react";
import { formatDate } from "@shared/utils";

interface EventCardPrimaryProps {
  event: Event;
}

/**
 * Primary event card molecule.
 * Large card (300×494) used in the main carousel slider.
 * Parent (CardSlider) handles click logic based on isActive.
 */
export function EventCardPrimary({ event }: EventCardPrimaryProps) {
  return (
    <article
      className="flex-shrink-0 overflow-hidden rounded-[9px] bg-neutral-50 cursor-pointer transition-transform hover:scale-[1.02]"
      style={{ width: 300, height: 494 }}
    >
      {/* Event image */}
      <div className="h-[300px] w-[300px] overflow-hidden">
        {event.image ? (
          <img
            src={event.image}
            alt={event.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-primary-100/20">
            <Text variant="body-sm" className="text-white/40">
              Sin imagen
            </Text>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-2 p-4">
        {/* Category */}
        <Text variant="h5" as="span" className="capitalize text-secondary-50">
          {event.category}
        </Text>

        {/* Event name */}
        <div style={{ minHeight: 80 }}>
          <Text
            variant="h1"
            as="h3"
            className="text-white leading-tight line-clamp-2"
            style={{ fontSize: 32 }}
            title={event.name}
          >
            {event.name}
          </Text>
        </div>

        {/* Location and date inline */}
        <div className="mt-3 flex items-center text-white">
          <MapPin size={17.75} className="flex-shrink-0" />
          <Text variant="body-sm" as="span" className="ml-1 text-white">
            {event.location.venue ||
              (event.location.city && event.location.country
                ? `${event.location.city}, ${event.location.country}`
                : event.location.city || event.location.country || "Ecuador")}
          </Text>
          <div className="mx-[13px]" />
          <Calendar size={17.75} className="flex-shrink-0" />
          <Text variant="body-sm" as="span" className="ml-1 text-white">
            {formatDate(event.dateevent)}
          </Text>
        </div>
      </div>
    </article>
  );
}
